import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FadeUp } from "@/components/Reveal";
import { API } from "@/data/site";

const inputCls =
  "w-full rounded-lg border border-espresso/20 bg-transparent px-4 py-3 text-sm text-espresso placeholder:text-cocoa/50 focus:border-mauve focus:ring-1 focus:ring-mauve";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your note is on its way. We reply within a day.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-cream py-24 lg:py-32" data-testid="contact-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <FadeUp className="lg:col-span-5">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sienna">Say hello</p>
          <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-espresso sm:text-5xl">
            Every journey begins with a <span className="italic text-sienna">conversation</span>
          </h2>
          <div className="mt-10 space-y-4 text-base text-cocoa">
            <p>
              Whether you are expecting, newly delivered, or still gathering your people —
              write to me, and together we will find the shape of care that fits your season.
            </p>
            <p className="text-sm">
              <span className="font-medium uppercase tracking-[0.15em] text-espresso">Email Athena directly</span>
              <br />
              <a href="mailto:athena@inbornbirth.com" data-testid="contact-email-link" className="text-sienna hover:underline">
                athena@inbornbirth.com
              </a>
            </p>
            <p className="text-sm">
              <span className="font-medium uppercase tracking-[0.15em] text-espresso">Website questions</span>
              <br />
              <a href="mailto:care@inbornbirth.com" data-testid="contact-care-email-link" className="text-sienna hover:underline">
                care@inbornbirth.com
              </a>
            </p>
            <p className="text-sm">
              <span className="font-medium uppercase tracking-[0.15em] text-espresso">Visits</span>
              <br />
              Serving families in-home throughout Northern Colorado. Virtual available wherever you are.
            </p>
          </div>
        </FadeUp>

        <FadeUp delay={0.15} className="lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <input required placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
              <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
            </div>
            <input placeholder="Subject (optional)" value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject-input" />
            <textarea required rows={5} placeholder="Tell us where you are in your journey…" value={form.message} onChange={set("message")} className={inputCls} data-testid="contact-message-input" />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="rounded-full bg-espresso px-8 py-3.5 text-sm font-medium uppercase tracking-[0.12em] text-ivory transition-colors hover:bg-sienna disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send enquiry"}
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
}
